import { parseBooleanVal, truncateDecimal } from "Utils/utils";

export const parseCodeAllocationRate = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : null;
};

/** Applied Rate display — keep 2 decimals by truncating, never rounding up. */
export const formatAppliedRateExact = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return truncateDecimal(n, 2).toFixed(2);
};

/** Participant count used for ratio split — matches backend funding assessment. */
export const resolveBillingParticipantCount = (shiftDetails) =>
  Math.max(1, shiftDetails?.participants?.length || 1);

export const resolveCatalogueRate = (
  ndisRateField,
  rateAmountField,
  fallbackRate
) => {
  if (ndisRateField === "alternate") {
    return parseCodeAllocationRate(rateAmountField);
  }
  return parseCodeAllocationRate(
    ndisRateField ?? rateAmountField ?? fallbackRate
  );
};

const isAlternateRateItem = (item) => {
  if (!item) return false;
  const flag = item.alternate_rate;
  return (
    flag === true ||
    flag === 1 ||
    flag === "1" ||
    parseBooleanVal(flag)
  );
};

export const getBreakdownCataloguePrice = (item) => {
  const selected = item?.item_numbers?.selected;
  if (selected?.price != null && selected.price !== "") {
    return selected.price;
  }
  if (item?.invoice_price != null && item.invoice_price !== "") {
    return item.invoice_price;
  }
  if (item?.ndis_rate != null && item.ndis_rate !== "") {
    return item.ndis_rate;
  }
  return null;
};

const breakdownUsesAlternateRate = (item, cataloguePrice) => {
  if (!item) return false;
  if (isAlternateRateItem(item)) return true;
  if (
    item.alternate_rate === false ||
    item.alternate_rate === 0 ||
    item.alternate_rate === "0"
  ) {
    return false;
  }
  if (cataloguePrice == null || cataloguePrice === "") return false;
  const saved = Number(item?.ndis_rate ?? item?.invoice_price);
  const catalogue = Number(cataloguePrice);
  return (
    !Number.isNaN(saved) &&
    !Number.isNaN(catalogue) &&
    saved !== catalogue
  );
};

const hasUsableFormRate = (formNdisRate, formRateAmount) => {
  if (formNdisRate === "alternate") {
    return parseCodeAllocationRate(formRateAmount) != null;
  }
  if (formNdisRate != null && formNdisRate !== "") {
    return (
      parseCodeAllocationRate(formNdisRate) != null ||
      parseCodeAllocationRate(formRateAmount) != null
    );
  }
  return parseCodeAllocationRate(formRateAmount) != null;
};

/** Resolve billing rate for a breakdown row — form first, then API/saved item. */
export const resolveBreakdownSegmentRate = (item, watchFormData, index) => {
  const formNdisRate = watchFormData?.[`invoice_ndis_rate_${index}`];
  const formRateAmount = watchFormData?.[`rate_amount_${index}`];
  const cataloguePrice = getBreakdownCataloguePrice(item);

  if (hasUsableFormRate(formNdisRate, formRateAmount)) {
    const fromForm = resolveCatalogueRate(
      formNdisRate,
      formRateAmount,
      cataloguePrice ?? item?.invoice_price ?? item?.ndis_rate
    );
    if (fromForm != null) {
      return fromForm;
    }
  }

  if (breakdownUsesAlternateRate(item, cataloguePrice)) {
    const alternateAmount =
      item?.ndis_rate ??
      item?.item_numbers?.selected?.alternate_price ??
      item?.invoice_price;
    const parsed = parseCodeAllocationRate(alternateAmount);
    if (parsed != null) return parsed;
  }

  return parseCodeAllocationRate(
    item?.invoice_price ??
      cataloguePrice ??
      item?.ndis_rate ??
      item?.item_numbers?.selected?.price
  );
};

/**
 * Per-participant applied hourly rate — same formula as legacy TimesheetContent /
 * TimesheetBreakdown and rosterInvoiceLineTotal (bill rate ÷ participant count).
 */
export const computeAppliedRateFromBillRate = (
  billRate,
  shiftDetailsOrParticipantCount
) => {
  const parsed = parseCodeAllocationRate(billRate);
  if (parsed == null) return "";

  const participantCount =
    typeof shiftDetailsOrParticipantCount === "number"
      ? Math.max(1, shiftDetailsOrParticipantCount)
      : resolveBillingParticipantCount(shiftDetailsOrParticipantCount);

  const applied = truncateDecimal(parsed / participantCount);
  return applied.toFixed(2);
};

export const resolveCodeAllocationRateStripValues = ({
  watchFormData,
  shiftDetails,
  ndisItemName,
  timesheetBreakdown,
  breakdownSegmentCount,
  invoiceBreakdownEnabled,
}) => {
  if (
    invoiceBreakdownEnabled &&
    breakdownSegmentCount === 1 &&
    Array.isArray(timesheetBreakdown) &&
    timesheetBreakdown.length > 0
  ) {
    const item = timesheetBreakdown[0];
    const catalogue = getBreakdownCataloguePrice(item);
    const billRate = resolveBreakdownSegmentRate(item, watchFormData, 0);
    return {
      catalogue,
      billRate,
      appliedRate: computeAppliedRateFromBillRate(billRate, shiftDetails),
    };
  }

  const billRate = resolveCatalogueRate(
    watchFormData?.invoice_ndis_rate,
    watchFormData?.rate_amount,
    ndisItemName?.price ?? shiftDetails?.invoice_price
  );
  const catalogue = ndisItemName?.price ?? billRate ?? null;

  return {
    catalogue,
    billRate,
    appliedRate: computeAppliedRateFromBillRate(billRate, shiftDetails),
  };
};
